'use client';

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { ShopperProducts } from '@/scapi';
import { Region, type ComponentType } from '@/components/region';
import { CATEGORY_SUBMENU_SLOT_CONTENT_REGION_ID } from '@/extensions/snk/lib/category-submenu-slots';
import { cn } from '@/lib/utils';
import { CategorySubmenuSlotMetadata } from '@/components/page-designer/category-submenu-slot/index';
import { withComponentRegionDesignMetadata } from '@/extensions/snk/lib/merge-component-region-metadata';
import type { MenuItemOverrideEntry } from '@/extensions/snk/lib/menu-item-list-overrides';
import { DEFAULT_MENU_ITEM_ICON_SIZE_PX } from '@/components/page-designer/menu-item/menu-item-config';
import { Link } from '@/shared-ui/atoms';
import { MenuItem } from '@/shared-ui/molecules/menu-item';
import { ChevronRightIcon } from '@/shared-ui/icons';
import { resolveMenuItemIcon } from '@/components/page-designer/menu-item/menu-item-icons';
/** @sfdc-extension-line SNK */
import { getCategoryHref, hasBrowsableChildren, type MenuItemListOverrides } from '@/extensions/snk/lib/utils';
import { BurgerMenuSubmenuHeader } from '../submenu-header';
import { BurgerMenuSubmenuPdContent } from '../submenu-pd-content';

const HEX_COLOR_REGEX = /^#(?:[0-9A-Fa-f]{3}){1,2}$/;

/** Typography variant for menu items shown below the root navigation level. */
const SUBMENU_TEXT_VARIANT = 'h4' as const;

function toSafeHexColor(value: string | undefined): string | undefined {
    if (!value) {
        return undefined;
    }

    return HEX_COLOR_REGEX.test(value) ? value : undefined;
}

type BurgerMenuNavigationProps = {
    categories: ShopperProducts.schemas['Category'][];
    overrideItems?: MenuItemOverrideEntry[];
    menuOverrides: MenuItemListOverrides;
    activeCategory?: ShopperProducts.schemas['Category'];
    showBrandsEntry: boolean;
    onOpenBrands: () => void;
    onOpenSubmenu: (category: ShopperProducts.schemas['Category']) => void;
    onBack: () => void;
    onCloseMenu: () => void;
    onNavigateToCategory: (category: ShopperProducts.schemas['Category']) => void;
    /** Page Designer slot instance for the active category submenu (content region below links). */
    categorySubmenuSlot?: ComponentType;
};

export const BurgerMenuNavigation = ({
    categories,
    overrideItems,
    menuOverrides,
    activeCategory,
    showBrandsEntry,
    onOpenBrands,
    onOpenSubmenu,
    onBack,
    onCloseMenu,
    onNavigateToCategory,
    categorySubmenuSlot,
}: BurgerMenuNavigationProps): ReactElement => {
    const { t } = useTranslation('header');
    const isSubmenu = Boolean(activeCategory);
    const isOverrideSubmenu = Boolean(overrideItems?.length);

    return (
        <div className="flex flex-col">
            {isSubmenu && activeCategory ? (
                <>
                    <BurgerMenuSubmenuHeader title={activeCategory.name ?? activeCategory.id} onBack={onBack} />

                    <MenuItem
                        textAlign="left"
                        showTopBorder
                        showBottomBorder
                        textWeight="medium"
                        textVariant={SUBMENU_TEXT_VARIANT}
                        className="rounded-none uppercase"
                        asChild>
                        <Link to={getCategoryHref(activeCategory)} onClick={onCloseMenu}>
                            {t('burgerMenu.shopAll', {
                                category: activeCategory.name,
                                defaultValue: 'Shop all {{category}}',
                            })}
                        </Link>
                    </MenuItem>
                </>
            ) : null}

            {isOverrideSubmenu ? (
                <ul className="list-none">
                    {overrideItems?.map((item) => {
                        const sharedMenuItemProps = {
                            textAlign: item.textAlign ?? 'left',
                            textWeight: item.textWeight,
                            textVariant: item.textVariant ?? SUBMENU_TEXT_VARIANT,
                            iconSizePx: item.iconSizePx ?? DEFAULT_MENU_ITEM_ICON_SIZE_PX,
                            removeHorizontalPadding: item.removeHorizontalPadding,
                            width: item.width,
                            backgroundColor: toSafeHexColor(item.backgroundColor),
                            hoverBackgroundColor: toSafeHexColor(item.hoverColor),
                            showTopBorder: item.showTopBorder,
                            showBottomBorder: item.showBottomBorder ?? true,
                            leftIcon: resolveMenuItemIcon(item.leftIcon),
                            rightIcon: resolveMenuItemIcon(item.rightIcon),
                            className: 'rounded-none uppercase',
                        } as const;

                        return (
                            <li key={item.id}>
                                {item.to ? (
                                    <MenuItem {...sharedMenuItemProps} asChild>
                                        <Link to={item.to} onClick={onCloseMenu}>
                                            {item.label}
                                        </Link>
                                    </MenuItem>
                                ) : (
                                    <MenuItem {...sharedMenuItemProps}>{item.label}</MenuItem>
                                )}
                            </li>
                        );
                    })}
                </ul>
            ) : (
                <ul className="list-none">
                    {!isSubmenu ? (
                        <>
                            <li>
                                <MenuItem
                                    textAlign="left"
                                    showTopBorder
                                    showBottomBorder
                                    className="rounded-none uppercase"
                                    asChild>
                                    {/* TODO: replace placeholder with environment specific offers page route */}
                                    <Link to="https://www.spacenk.com/uk/offers" onClick={onCloseMenu}>
                                        {t('burgerMenu.offers', 'Offers')}
                                    </Link>
                                </MenuItem>
                            </li>
                            {showBrandsEntry ? (
                                <li>
                                    <MenuItem
                                        textAlign="left"
                                        showBottomBorder
                                        rightIcon={ChevronRightIcon}
                                        className="rounded-none uppercase"
                                        onClick={onOpenBrands}>
                                        {t('burgerMenu.brands', 'Brands')}
                                    </MenuItem>
                                </li>
                            ) : null}
                        </>
                    ) : null}

                    {categories.map((category) => {
                        const showChildren = hasBrowsableChildren(category, menuOverrides);
                        const submenuTypography = isSubmenu ? { textVariant: SUBMENU_TEXT_VARIANT } : {};

                        if (showChildren) {
                            return (
                                <li key={category.id}>
                                    <MenuItem
                                        textAlign="left"
                                        showBottomBorder
                                        rightIcon={ChevronRightIcon}
                                        className={cn('rounded-none uppercase')}
                                        onClick={() => onOpenSubmenu(category)}
                                        {...submenuTypography}>
                                        {category.name}
                                    </MenuItem>
                                </li>
                            );
                        }

                        return (
                            <li key={category.id}>
                                <MenuItem
                                    textAlign="left"
                                    showBottomBorder
                                    className="rounded-none uppercase"
                                    onClick={() => onNavigateToCategory(category)}
                                    {...submenuTypography}>
                                    {category.name}
                                </MenuItem>
                            </li>
                        );
                    })}
                </ul>
            )}

            {isSubmenu && categorySubmenuSlot ? (
                <BurgerMenuSubmenuPdContent testId="burger-menu-category-submenu-content">
                    <Region
                        component={withComponentRegionDesignMetadata(categorySubmenuSlot, CategorySubmenuSlotMetadata)}
                        regionId={CATEGORY_SUBMENU_SLOT_CONTENT_REGION_ID}
                        errorElement={null}
                    />
                </BurgerMenuSubmenuPdContent>
            ) : null}
        </div>
    );
};
